import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchBanks } from "../../core/redux/slices/bankSlice";
import { getBankLedgerReport } from "../../core/api/reportService";
import { exportToCsv } from "../../core/utils/exportCsv";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const dateOnly = (iso) => (iso ? iso.split("T")[0] : "");

// GET /api/reports/bank-ledger?bankId=&fromDate=&toDate= (HANDOVER_FRONTEND 3.md) returns
// the running ledger balance already computed server-side, seeded from the bank's
// openingBalance — no client-side merge/running-total math needed here. toDate is now
// correctly inclusive of the full day (fixed 2026-08-18) — the old "query one extra day
// and trim client-side" workaround for same-day Invoice Payments is no longer needed.
// Journal entries tagged to a bank are deliberately excluded server-side (a journal only
// carries one bankID shared across its debit/credit side, so there's no reliable way to
// classify it as In vs Out yet) — still worth surfacing that to the user in the UI.
//
// The ledger's running `balance` is seeded from the bank's static `openingBalance` for
// whatever range is queried (confirmed in HANDOVER_FRONTEND 3.md §6) — there's no per-date
// opening balance server-side. So querying just [dateFrom, dateTo] would re-seed from the
// bank's original static balance every time, ignoring everything that happened before
// dateFrom. To get the true opening balance as of dateFrom, pull the ledger from a fixed
// epoch (well before any real transaction) through dateTo, and take whatever the running
// `balance` was on the last row strictly before dateFrom — that's the prior day's actual
// closing balance. Falls back to the bank's static openingBalance when there's no history
// before dateFrom (e.g. bank's first day), matching the "empty history" behavior documented
// for this endpoint (returns `[]`, not a seeded opening-balance row, when there's no activity).
const LEDGER_EPOCH = "2000-01-01";

const BankReport = () => {
  const dispatch = useDispatch();
  const banks = useSelector((s) => s.bank.list);

  const [bankId, setBankId] = useState("");
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [rows, setRows] = useState(null);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => { dispatch(fetchBanks()); }, [dispatch]);

  const bankOptions = banks.map((b) => ({ value: b.bankID || b.id, label: b.bankName }));
  const selectedBank = banks.find((b) => (b.bankID || b.id) === bankId);

  const runReport = async () => {
    if (!bankId) { Swal.fire("Required", "Please select a bank.", "warning"); return; }
    setLoading(true);
    try {
      // Query from the epoch (not dateFrom) so the backend's running `balance` on each
      // row reflects true all-time cumulative balance, not one that restarts from the
      // bank's static openingBalance at the start of the picked range.
      const res = await getBankLedgerReport({ bankId, from: LEDGER_EPOCH, to: dateTo });
      const allRows = res.data || [];
      const priorRows = allRows.filter((r) => dateOnly(r.date) < dateFrom);
      const inRange = allRows
        .filter((r) => dateOnly(r.date) >= dateFrom)
        .map((r, idx) => ({ ...r, slNo: idx + 1 }));
      setOpeningBalance(priorRows.length ? Number(priorRows[priorRows.length - 1].balance) : Number(selectedBank?.openingBalance || 0));
      setRows(inRange);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load the bank report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const closingBalance = rows && rows.length ? Number(rows[rows.length - 1].balance) : openingBalance;

  const handleExport = () => {
    const headers = ["Sl.No", "Date", "Particulars", "Accounts Head", "Remarks", "In", "Out", "Balance"];
    const dataRows = rows.map((r) => [
      r.slNo, dateOnly(r.date), r.particulars || "", r.accountsHead || "", r.remarks || "", r.in, r.out, r.balance,
    ]);
    dataRows.unshift(["", "", "", "", "Opening Balance", "", "", openingBalance]);
    dataRows.push(["", "", "", "", "Closing Balance", "", "", closingBalance]);
    exportToCsv(`Bank-Report-${selectedBank?.bankName}-${dateFrom}_to_${dateTo}.csv`, headers, dataRows);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Bank Report</h4><h6>Running ledger of money in/out of a bank account</h6></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto" style={{ minWidth: 220 }}>
                <label className="form-label small mb-1">Bank</label>
                <Select classNamePrefix="react-select" options={bankOptions}
                  value={bankOptions.find((o) => o.value === bankId) || null}
                  onChange={(opt) => { setBankId(opt?.value || ""); setRows(null); }}
                  placeholder="Select bank" isClearable />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={runReport} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-success" onClick={handleExport} disabled={!rows || rows.length === 0}>
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {rows && (
          <>
            <div className="row mb-3">
              <div className="col-6 col-md-3 mb-2">
                <div className="card border-secondary text-center py-2">
                  <div className="text-secondary fs-6 fw-bold">{fmt(openingBalance)}</div>
                  <div className="small text-muted">Opening Balance</div>
                </div>
              </div>
              <div className="col-6 col-md-3 mb-2">
                <div className={`card border-${closingBalance >= 0 ? "success" : "danger"} text-center py-2`}>
                  <div className={`text-${closingBalance >= 0 ? "success" : "danger"} fs-6 fw-bold`}>{fmt(closingBalance)}</div>
                  <div className="small text-muted">Closing Balance</div>
                </div>
              </div>
            </div>

            <div className="alert alert-warning py-2 px-3 small">
              Journal entries tagged to a bank are not included above — a journal entry&apos;s debit/credit lines don&apos;t
              indicate which side represents this bank account, so it can&apos;t be reliably classified as In or Out yet.
            </div>

            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Sl.No</th>
                        <th>Date</th>
                        <th>Particulars</th>
                        <th>Accounts Head</th>
                        <th>Remarks</th>
                        <th className="text-end">In</th>
                        <th className="text-end">Out</th>
                        <th className="text-end">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="table-light">
                        <td colSpan={7} className="text-end fw-semibold">Opening Balance</td>
                        <td className="text-end fw-bold">{fmt(openingBalance)}</td>
                      </tr>
                      {rows.length === 0 ? (
                        <tr><td colSpan={8} className="text-center text-muted py-3">No transactions in this date range.</td></tr>
                      ) : rows.map((r) => (
                        <tr key={r.slNo}>
                          <td>{r.slNo}</td>
                          <td>{r.date ? new Date(r.date).toLocaleDateString("en-IN") : "—"}</td>
                          <td>{r.particulars || "—"}</td>
                          <td>{r.accountsHead || "—"}</td>
                          <td>{r.remarks || "—"}</td>
                          <td className="text-end">{r.in ? fmt(r.in) : "—"}</td>
                          <td className="text-end">{r.out ? fmt(r.out) : "—"}</td>
                          <td className="text-end fw-semibold">{fmt(r.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {!rows && !loading && (
          <div className="card text-center py-5 text-muted">
            <p>Select a bank and date range, then click <strong>Search</strong> to view its ledger.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankReport;
